import { useEffect, useState } from 'react'

interface UseGravatarArgs {
  email: string | null | undefined
  width?: number
  height?: number
  defaultType?:
    | 'mp'
    | 'identicon'
    | 'monsterid'
    | 'wavatar'
    | 'retro'
    | 'robohash'
    | 'blank'
}

/**
 * Builds a Gravatar URL for the given email with a size that respects device pixel ratio.
 * Uses SHA-256 per Gravatar docs.
 *
 * Reference: https://docs.gravatar.com/rest/getting-started/
 */
export const useGravatar = ({
  email,
  width = 32,
  height = 32,
  defaultType = 'mp',
}: UseGravatarArgs): string | undefined => {
  const [url, setUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    const trimmed = (email ?? '').trim().toLowerCase()
    if (!trimmed) {
      setUrl(undefined)
      return
    }

    const computeHash = async () => {
      try {
        // Use Web Crypto API (available in all modern browsers)
        const encoder = new TextEncoder()
        const data = encoder.encode(trimmed)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        const hash = hashArray
          .map((b) => b.toString(16).padStart(2, '0'))
          .join('')

        const dpr =
          typeof window !== 'undefined' && window.devicePixelRatio
            ? window.devicePixelRatio
            : 1
        const logical = Math.max(width, height) * 3
        const requestedSize = Math.round(
          Math.min(Math.max(logical * dpr, 16), 256),
        )

        // we use d=404 to avoid Gravatar returning a default image when the email is not found
        setUrl(
          `https://0.gravatar.com/avatar/${hash}?s=${requestedSize}&d=${encodeURIComponent(defaultType)}&d=404`,
        )
      } catch {
        // If hashing fails for any reason, skip Gravatar
        setUrl(undefined)
      }
    }

    computeHash()
  }, [email, width, height, defaultType])

  return url
}
